console.log("Rabbl launched");

import { Platform, StyleSheet } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';
import 'react-native-reanimated';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from "@/utilities/AppUtils";
import { LogBox } from 'react-native';
import { ErrorBoundaryProps } from 'expo-router';
import { CustomErrorBoundary } from "@/components/CustomErrorBoundary";
import "react-datepicker/dist/react-datepicker.css";
import { useCallback } from 'react';
import { Dimensions } from 'react-native';
import { useEffect } from 'react';
import AppBackground from '@/components/AppBackground';
import * as AppCore from "@/utilities/AppCore";
import { store } from '@/state/store';
import { Provider } from 'react-redux';
import { StatusBar } from 'expo-status-bar';
import { PlatformSpecificRootComponent } from '@/components/PlatformSpecificRootComponent';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Toaster } from '@/components/CustomSonner';
import { Slot } from 'expo-router';
import { CustomSafeAreaView } from '@/components/CustomSafeAreaView';
import { SafeAreaProvider } from 'react-native-safe-area-context';

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

AppUtilities.info(
  "App started: " +
  "version: " + AppConstants.VERSION + ", " +
  "platform: " + Platform.OS + ", " +
  "devMode: " + AppUtils.isDevelopment()
);

if (!AppConstants.IS_DEVELOPMENT) {
  LogBox.ignoreAllLogs(true);

  const rejectionTracking = require('promise/setimmediate/rejection-tracking');
  
  rejectionTracking.enable({
    allRejections: true,
    onUnhandled: (id: number, error: Error) => {
      console.log('Uncaught Promise Rejection: ' + AppUtilities.getErrors(error), error);
      try {
        AppUtilities.handleException(error, "rejectionTracking");
      } catch (e) {
        console.log(e);
      }
    },
  });
}

// https://docs.expo.dev/router/error-handling/
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {

  const onDismiss = useCallback(async () => {
    // The main ErrorBoundary is actually at the (contextful) layer
    // which has access to Redux so it can safely log the user out.
    // This only gets executed if something fails in the components below
    // which is pretty unlikely.
    retry();
  }, [error]);

  return (
    <CustomErrorBoundary
      title="Fatal error"
      errorMessage={"A fatal error has occurred: " + AppUtilities.getErrors(error)}
      dismissText="OK"
      onDismiss={onDismiss}
      error={error}
    />
  );
}

const styles = StyleSheet.create({
  rootView: {
    flex: 1,
  },
});

export default function LayoutStateful() {

  useEffect(() => {
    try {
      const localData = AppUtilities.loadLocalData();

      if (localData && AppUtilities.DEBUGLEVEL.extra && Object.keys(localData).length > 0) {
        AppUtilities.info("Saved state:");
        AppUtilities.dir(localData);
      }

      const dimensions = Dimensions.get('window');
      AppUtilities.info(
        "App context: " +
        "logical host: " + AppCore.getLogicalHost() + ", " +
        "actual host: " + AppUtilities.getActualHost(false, true) + ", " +
        "w: " + dimensions.width + ", " +
        "h: " + dimensions.height + ", " +
        "loggedIn: " + AppCore.isLoggedIn()
      );

      AppUtils.initializeOneTimeFast();

      SplashScreen.hideAsync();
    } catch (e) {
      AppUtilities.handleError(e);
    }
  }, []);

  // The root layout sets up any global wrappers as well as our main state context (i.e. Redux).
  // Nothing expensive (i.e. might easily re-render) should be put here.
  return (
    <Provider store={store}>
      <GestureHandlerRootView style={styles.rootView}>
        <AppBackground>
          <SafeAreaProvider>
            <CustomSafeAreaView>
              <Slot />
              <StatusBar style="dark" />
              <PlatformSpecificRootComponent />
              <Toaster />
            </CustomSafeAreaView>
          </SafeAreaProvider>
        </AppBackground>
      </GestureHandlerRootView>
    </Provider>
  );
}
